/**
 * DealModalSidebar - Sidebar contextual do modal premium de Negócios
 *
 * Versão: 3.0.0
 * RELEASE: 2026-05-28 v3.0.0 - Padronização visual com TaskModalSidebar (cards flutuantes)
 *   - Container `bg-muted/30 border-l border-border/40`, `space-y-3`
 *   - Cada seção dentro de `div.rounded-xl border bg-card shadow-sm p-3`
 *   - Cabeçalhos com ícone-âncora à direita (`h-3.5 w-3.5 text-muted-foreground`)
 *   - Removidos `<Separator />` entre seções (substituídos pelo agrupamento em cards)
 *   - Prioridade + Probabilidade unidas no card "Status Comercial"
 * RELEASE: 2026-02-09 v2.0.0 - Removidos seletores inline; ícone Pencil abre DealInfoEditDialog
 */
import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Layers,
  Target,
  Calendar,
  CalendarPlus,
  CalendarCheck,
  Link2,
  Sparkles,
  TrendingUp,
  Trophy,
  XCircle,
  Pencil,
  Briefcase,
  User,
  BadgeCheck,
  ExternalLink,

  Tags,
  Flag,
  Users,
  Activity,
  Handshake,
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { useIsMobile } from '@/hooks/use-mobile';
import { DealCloseDateEditDialog } from './components/DealCloseDateEditDialog';
import { formatDateOnlyBR } from '@/lib/utils/date-only';

import { cn } from '@/lib/utils';
import { CrmDeal } from '@/hooks/useCrmDeals';
import { useCrmLossReasons } from '@/hooks/useCrmLossReasons';
import { ScrollArea } from '@/components/ui/scroll-area';
import { DealContactCard } from './components/DealContactCard';
import { DealCustomerCard } from './components/DealCustomerCard';
import { DealQuickInfoCard } from './components/DealQuickInfoCard';
import { DealPriorityBadgePremium } from './components/DealPriorityBadgePremium';
import { ActivityFormSheet } from './components/ActivityFormSheet';
import { DealPeopleSection } from './components/DealPeopleSection';
import { DealTagsSection } from './components/DealTagsSection';
import { DealInfoEditDialog } from './components/DealInfoEditDialog';
import { DealPeopleEditDialog } from './components/DealPeopleEditDialog';
import { DealReferralSection } from './components/DealReferralSection';
import { DealSchedulingLinkDialog } from './components/DealSchedulingLinkDialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Progress } from '@/components/ui/progress';

import { LeadSourceBadge } from '@/components/crm/deals/components/LeadSourceBadge';
import { DealOriginBadge } from '@/components/crm/deals/components/DealOriginBadge';
import type { QuickActivityType } from './components/QuickActivityBar';
// v3.2.0: trava do Workflow explicada antes do clique na ação rápida Ganho
import { WorkflowGuardGate } from '@/components/workflow/WorkflowGuardGate';
import type { WorkflowGuardDecision } from '@/hooks/useWorkflowGuardCheck';

// Props do componente
interface DealModalSidebarProps {
  deal: CrmDeal;
  canMarkAsWon?: boolean;
  onViewContact?: (contactId: string) => void;
  onViewCustomer?: (customerId: string) => void;
  /** v1.3.0: Callback para abrir cadastro de cliente a partir do contato */
  onCreateCustomer?: (contactId: string) => void;
  onInfoSave: (data: {
    pipeline_id: string;
    phase_id: string;
    expected_close_date: string | null;
    source: string | null;
  }) => Promise<void> | void;
  isInfoSaving?: boolean;
  /** v3.1.0: libera o lápis de edição de Responsável/Vendedor (mesma regra do botão Editar) */
  canEditPeople?: boolean;
  /** v3.1.0: persiste responsável/vendedor */
  onPeopleSave?: (data: {
    assigned_to: string | null;
    seller_id: string | null;
  }) => Promise<void> | void;
  isPeopleSaving?: boolean;
  onMarkWon: () => void;
  onMarkLost: () => void;
  /**
   * v3.2.0: decisão antecipada do Workflow para `crm.deal.win`.
   * Quando `bloqueado`, a ação rápida Ganho fica desabilitada com tooltip.
   */
  workflowGuardDecision?: WorkflowGuardDecision | null;
}

/**
 * Wrapper visual reutilizado em todas as seções (mesmo padrão de TaskModalSidebar)
 * Card flutuante com header de título + ícone-âncora opcional à direita.
 */
function SectionCard({
  title,
  icon: Icon,
  action,
  children,
}: {
  title: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Elemento opcional à direita do header (ex.: botão Pencil) */
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-border bg-card shadow-sm p-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
        </h4>
        {action ?? (Icon && <Icon className="h-3.5 w-3.5 text-muted-foreground" />)}
      </div>
      {children}
    </div>
  );
}

/**
 * Sidebar contextual (360px fixa) — padrão visual unificado com TaskModalSidebar
 */
export function DealModalSidebar({
  deal,
  canMarkAsWon = true,
  onViewContact,
  onViewCustomer,
  onCreateCustomer,
  onInfoSave,
  isInfoSaving = false,
  canEditPeople = false,
  onPeopleSave,
  isPeopleSaving = false,
  onMarkWon,
  onMarkLost,
  workflowGuardDecision = null,
}: DealModalSidebarProps) {
  // Estados para o sheet de atividade rápida
  const [activitySheetOpen, setActivitySheetOpen] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState<QuickActivityType | null>(null);

  // v2.0.0: Estado para modal de edição de informações
  const [infoEditOpen, setInfoEditOpen] = useState(false);

  // v3.1.0: Estado para modal de edição de Responsável/Vendedor
  const [peopleEditOpen, setPeopleEditOpen] = useState(false);

  // v2.3.0: Estado para modal de edição da data de conclusão
  const [closeDateEditOpen, setCloseDateEditOpen] = useState(false);

  // v1.3.0: Estado para dialog de agendamento vinculado ao deal
  const [schedulingOpen, setSchedulingOpen] = useState(false);

  // v2.3.0: Permissão para alterar data de conclusão (admins ou perfil com a flag)
  const { canView } = usePermissions();
  const isMobile = useIsMobile();
  const canEditCloseDate = canView('crm_param_edit_close_date');

  // Handler para clique em ícone de atividade
  const handleQuickActivity = (type: QuickActivityType) => {
    if (type === 'scheduling') {
      setSchedulingOpen(true);
      return;
    }
    setSelectedActivityType(type);
    setActivitySheetOpen(true);
  };

  /**
   * Handler wrapper para salvar informações e fechar modal automaticamente (v2.0.1)
   */
  const handleInfoSaveAndClose = async (data: {
    pipeline_id: string;
    phase_id: string;
    expected_close_date: string | null;
    source: string | null;
  }) => {
    try {
      await onInfoSave(data);
      setInfoEditOpen(false);
    } catch (error) {
      // Em caso de erro, mantém o modal aberto. Toast já é tratado na mutation.
    }
  };

  /**
   * v3.1.0: Salva Responsável/Vendedor e fecha o modal em caso de sucesso
   */
  const handlePeopleSaveAndClose = async (data: {
    assigned_to: string | null;
    seller_id: string | null;
  }) => {
    try {
      await onPeopleSave?.(data);
      setPeopleEditOpen(false);
    } catch (error) {
      // Mantém aberto em caso de erro; toast tratado na mutation.
    }
  };

  const hasCustomer = !!deal.contact?.customer?.[0];

  return (
    <>
      <ScrollArea className={cn(
        'h-full shrink-0',
        isMobile
          ? 'w-full'
          : 'w-[360px] bg-muted/30 border-l border-border/40'
      )}>
        {/* v3.2.0: min-w-0/overflow-x-hidden blindam a sidebar contra filhos largos
            (ex.: e-mail longo no card de contato) que empurravam o layout para fora da borda */}
        <div className={cn('space-y-3 min-w-0 overflow-x-hidden', isMobile ? 'p-0' : 'p-3')}>
          {/* ═══════════════════════════════════════════ */}
          {/* CONTATO / CLIENTE                          */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard
            title={hasCustomer ? 'Cliente' : 'Contato'}
            icon={User}
            /* v3.3.0: selo + atalho "Abrir contato" movidos para o header da seção.
               Antes ficavam dentro do card, disputando espaço com nome/telefone/e-mail
               e empurrando a sidebar para fora da borda do modal. */
            action={
              deal.contact ? (
                <div className="flex items-center gap-1 shrink-0">
                  <Badge
                    variant={hasCustomer ? 'default' : 'outline'}
                    /* v3.3.2: contato mantém o estilo "budget" outline como estava antes;
                       cliente segue com fundo verde suave e sem borda visível. */
                    className={cn(
                      'gap-1 h-5 px-1.5 text-[10px] font-medium',
                      hasCustomer && 'bg-success/10 text-success hover:bg-success/15 border-transparent'
                    )}
                  >
                    {hasCustomer ? <BadgeCheck className="h-3 w-3" /> : <User className="h-3 w-3" />}
                    {hasCustomer ? 'Cliente' : 'Contato'}
                  </Badge>

                  {!hasCustomer && onViewContact && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-foreground"
                          onClick={() => onViewContact(deal.contact!.id)}
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="left">
                        <p className="text-xs">Abrir contato</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              ) : undefined
            }
          >
            {hasCustomer ? (
              <DealCustomerCard
                customer={deal.contact!.customer![0]}
                contact={deal.contact!}
                onViewCustomer={onViewCustomer}
                onQuickActivity={handleQuickActivity}
              />
            ) : (
              <DealContactCard
                contact={deal.contact || null}
                hasCustomer={false}
                onViewContact={onViewContact}
                onCreateCustomer={onCreateCustomer}
                onQuickActivity={handleQuickActivity}
              />
            )}
          </SectionCard>


          {/* ═══════════════════════════════════════════ */}
          {/* INFORMAÇÕES (workspace, pipeline, fase…)   */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard
            title="Informações"
            icon={Activity}
            action={
              deal.status === 'open' ? (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 text-muted-foreground hover:text-foreground"
                  onClick={() => setInfoEditOpen(true)}
                  title="Editar informações"
                >
                  <Pencil className="h-3 w-3" />
                </Button>
              ) : undefined
            }
          >
            <div className="space-y-1">
              {/* Workspace */}
              <div className="flex items-center gap-2 py-1.5 w-full">
                <Briefcase className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Workspace</span>
                <div className="flex items-center gap-1.5 ml-auto min-w-0">
                  {deal.pipeline?.workspace_color && (
                    <div
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: deal.pipeline.workspace_color }}
                    />
                  )}
                  <span className="text-xs font-medium truncate">
                    {deal.pipeline?.workspace_name || '—'}
                  </span>
                </div>
              </div>

              {/* Pipeline */}
              <DealQuickInfoCard
                icon={Layers}
                label="Pipeline"
                value={deal.pipeline?.name || '-'}
              />

              {/* Fase */}
              <div className="flex items-center gap-2 py-1.5 w-full">
                <Target className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Fase</span>
                <div className="flex items-center gap-1.5 ml-auto">
                  {deal.phase?.color && (
                    <div
                      className="h-2.5 w-2.5 rounded-full shrink-0"
                      style={{ backgroundColor: deal.phase.color }}
                    />
                  )}
                  <span className="text-xs font-medium truncate">
                    {deal.phase?.name || '-'}
                  </span>
                </div>
              </div>

              {/* Origem */}
              <div className="flex items-center gap-2 py-1.5 w-full">
                <Link2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Origem</span>
                <div className="ml-auto">
                  <LeadSourceBadge sourceId={deal.source} variant="inline" size="sm" />
                </div>
              </div>

              {/* Origem do cadastro */}
              <div className="flex items-center gap-2 py-1.5 w-full">
                <Sparkles className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Cadastro</span>
                <div className="ml-auto min-w-0">
                  <DealOriginBadge origin={deal.creation_origin ?? null} dealId={deal.id} />
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ═══════════════════════════════════════════ */}
          {/* INDICAÇÃO / AFILIADO (condicional)         */}
          {/* ═══════════════════════════════════════════ */}
          {deal.referral && (
            <SectionCard title="INDICAÇÃO / AFILIADO" icon={Handshake}>
              <DealReferralSection referral={deal.referral} />
            </SectionCard>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* DATAS                                       */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard title="Datas" icon={Calendar}>
            <div className="space-y-1">
              <DealQuickInfoCard
                icon={CalendarPlus}
                label="Cadastrado em"
                value={
                  deal.created_at
                    ? format(new Date(deal.created_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                    : '-'
                }
              />
              <DealQuickInfoCard
                icon={Calendar}
                label="Previsão"
                value={
                  deal.expected_close_date ? formatDateOnlyBR(deal.expected_close_date) : '-'
                }
              />
              {(deal.status === 'won' || deal.status === 'lost') && (
                <div className="flex items-center gap-2 py-1.5 w-full">
                  <CalendarCheck className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  <span className="text-xs text-muted-foreground shrink-0">Concluído em</span>
                  <div className="ml-auto flex items-center gap-1">
                    <span className="text-xs font-medium">
                      {deal.actual_close_date ? formatDateOnlyBR(deal.actual_close_date) : '-'}
                    </span>
                    {canEditCloseDate && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-5 w-5 text-muted-foreground hover:text-foreground"
                        onClick={() => setCloseDateEditOpen(true)}
                        title="Alterar data de conclusão"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {/* Motivo da perda (quando o negócio foi concluído como perdido) */}
              {deal.status === 'lost' && (deal as any).loss_reason_id && (
                <LossReasonRow lossReasonId={(deal as any).loss_reason_id} note={deal.loss_reason} />
              )}
            </div>
          </SectionCard>

          {/* ═══════════════════════════════════════════ */}
          {/* TAGS                                        */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard title="Tags" icon={Tags}>
            <DealTagsSection dealId={deal.id} tenantId={deal.tenant_id} />
          </SectionCard>

          {/* ═══════════════════════════════════════════ */}
          {/* STATUS COMERCIAL (Prioridade + Probabilidade) */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard title="Status Comercial" icon={Flag}>
            <div className="space-y-3">
              {/* Prioridade */}
              <div className="flex items-center gap-2 py-1.5 w-full">
                <Flag className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                <span className="text-xs text-muted-foreground shrink-0">Prioridade</span>
                <div className="ml-auto">
                  <DealPriorityBadgePremium priority={deal.priority} size="sm" variant="inline" />
                </div>
              </div>

              {/* Probabilidade */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">Probabilidade</span>
                  </div>
                  <span className="text-xs font-medium">{deal.probability ?? 0}%</span>
                </div>
                <Progress value={deal.probability ?? 0} className="h-2" />
              </div>
            </div>
          </SectionCard>

          {/* ═══════════════════════════════════════════ */}
          {/* AÇÕES RÁPIDAS (apenas se deal aberto)      */}
          {/* ═══════════════════════════════════════════ */}
          {deal.status === 'open' && (
            <SectionCard title="Ações Rápidas" icon={Sparkles}>
              <div className="grid grid-cols-2 gap-2">
                {/* v3.2.0: Gate explica a trava do Workflow antes do clique */}
                <WorkflowGuardGate decisao={workflowGuardDecision ?? { resultado: 'ALLOW', mensagem: null, bloqueado: false, avisa: false, carregando: false }} side="top">
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      'text-xs h-8 text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300 dark:border-emerald-800 dark:hover:bg-emerald-900/20',
                      (!canMarkAsWon || workflowGuardDecision?.bloqueado) && 'opacity-50 cursor-not-allowed'
                    )}
                    onClick={canMarkAsWon && !workflowGuardDecision?.bloqueado ? onMarkWon : undefined}
                    disabled={!canMarkAsWon || workflowGuardDecision?.bloqueado === true}
                    title={
                      workflowGuardDecision?.bloqueado
                        ? (workflowGuardDecision.mensagem ?? 'Existe uma pendência do processo neste negócio.')
                        : !canMarkAsWon
                          ? 'Adicione ao menos um produto/serviço'
                          : undefined
                    }
                  >
                    <Trophy className="h-3 w-3 mr-1" />
                    Ganho
                  </Button>
                </WorkflowGuardGate>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs h-8 text-rose-600 border-rose-200 hover:bg-rose-50 hover:border-rose-300 dark:border-rose-800 dark:hover:bg-rose-900/20"
                  onClick={onMarkLost}
                >
                  <XCircle className="h-3 w-3 mr-1" />
                  Perdido
                </Button>
              </div>
            </SectionCard>
          )}

          {/* ═══════════════════════════════════════════ */}
          {/* PESSOAS                                     */}
          {/* ═══════════════════════════════════════════ */}
          <SectionCard title="Pessoas" icon={Users}>
            <DealPeopleSection
              seller={deal.seller || null}
              assignedUser={deal.assigned_user || null}
              createdByUser={deal.created_by_user || null}
              createdAt={deal.created_at}
              onEditPeople={
                canEditPeople && onPeopleSave ? () => setPeopleEditOpen(true) : undefined
              }
            />
          </SectionCard>

        </div>
      </ScrollArea>

      {/* Sheet lateral para formulário de atividade
          Montagem condicional: evita instanciar useCrmDealActivities enquanto fechado */}
      {activitySheetOpen && (
        <ActivityFormSheet
          open={activitySheetOpen}
          onOpenChange={setActivitySheetOpen}
          activityType={selectedActivityType}
          dealId={deal.id}
        />
      )}

      {/* Modal de edição de informações do deal */}
      <DealInfoEditDialog
        open={infoEditOpen}
        onOpenChange={setInfoEditOpen}
        deal={deal}
        onSave={handleInfoSaveAndClose}
        isSaving={isInfoSaving}
      />

      {/* v3.1.0: Modal de edição de Responsável/Vendedor (montagem condicional) */}
      {peopleEditOpen && onPeopleSave && (
        <DealPeopleEditDialog
          open={peopleEditOpen}
          onOpenChange={setPeopleEditOpen}
          deal={deal}
          onSave={handlePeopleSaveAndClose}
          isSaving={isPeopleSaving}
        />
      )}



      {/* Dialog de agendamento vinculado ao deal */}
      <DealSchedulingLinkDialog
        open={schedulingOpen}
        onOpenChange={setSchedulingOpen}
        dealId={deal.id}
        dealTitle={deal.title}
      />

      {/* Modal para editar data de conclusão (apenas com permissão) */}
      {canEditCloseDate && (
        <DealCloseDateEditDialog
          open={closeDateEditOpen}
          onOpenChange={setCloseDateEditOpen}
          dealId={deal.id}
          currentValue={deal.actual_close_date}
        />
      )}
    </>
  );
}

/**
 * LossReasonRow - Exibe o motivo da perda (nome + bolinha de cor + observação opcional)
 * Isolado em subcomponente para chamar o hook useCrmLossReasons apenas quando necessário.
 */
function LossReasonRow({
  lossReasonId,
  note,
}: {
  lossReasonId: string;
  note?: string | null;
}) {
  const { reasons } = useCrmLossReasons(true);
  const reason = reasons.find((r) => r.id === lossReasonId);
  return (
    <div className="flex items-start gap-2 py-1.5 w-full">
      <XCircle className="h-3.5 w-3.5 text-destructive shrink-0 mt-0.5" />
      <span className="text-xs text-muted-foreground shrink-0">Motivo</span>
      <div className="ml-auto flex flex-col items-end gap-0.5 min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className="h-2 w-2 rounded-full ring-1 ring-border"
            style={{ backgroundColor: reason?.color ?? '#9ca3af' }}
          />
          <span className="text-xs font-medium truncate max-w-[180px]" title={reason?.name}>
            {reason?.name ?? 'Motivo removido'}
          </span>
        </div>
        {note && (
          <span
            className="text-[10px] text-muted-foreground italic truncate max-w-[200px]"
            title={note}
          >
            {note}
          </span>
        )}
      </div>
    </div>
  );
}
